import { CACHE_PROFILES } from "@/config/query-cache"
import { useTransportQuery, type TransportRequestError } from "@/transport"

import type { GetMetricRequest } from "@/types_api/backend/transport/get_metric_request"
import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  mapTransportMetricToRecordData,
  type TransportMetricResource,
} from "../utils/transportMetricData"

interface UseMetricRecordTransportOptions {
  metricId: string | undefined
  enabled?: boolean
}

interface MetricRecordTransportResult {
  metricRecord: (MetricRecordData & { id: string }) | undefined
  isLoading: boolean
  error: TransportRequestError | null
}

/**
 * Fetch one metric directly, for readers that cannot rely on the cached list.
 *
 * The list is a `lookup`-profile collection that does not refetch on mount, so
 * a metric created after it was cached is absent until something refetches it.
 */
export function useMetricRecordTransport({
  metricId,
  enabled = true,
}: UseMetricRecordTransportOptions): MetricRecordTransportResult {
  const queryEnabled = enabled && Boolean(metricId)
  const query = useTransportQuery({
    operation: "getMetric",
    request: { metric_id: metricId ?? "" } satisfies GetMetricRequest,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: queryEnabled,
      reportErrors: true,
      retry: false,
      select: (response) =>
        mapTransportMetricToRecordData(
          response.metric as TransportMetricResource,
        ),
    },
  })

  return {
    metricRecord: queryEnabled ? query.data : undefined,
    isLoading: queryEnabled && query.isLoading,
    error: queryEnabled ? query.error : null,
  }
}
